import { useState } from 'react';
import clsx from 'clsx';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MEDIA_STATE_LABELS,
  QUALITY_LABELS,
  type MovieDetail as MovieDetailType,
  type ReleaseDTO,
} from '@jelly-dl/shared';
import { api, ApiError } from '../api/client';
import { Badge, Button, Card, ErrorState, ProgressBar, Spinner, Toggle } from '../components/ui';
import { GRAB_STATUS_COLORS, MEDIA_STATE_COLORS, QUALITY_COLORS, humanBytes } from '../lib/format';

const ACTIVE_STATES = ['searching', 'grabbed', 'downloading', 'importing'];

export function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [msg, setMsg] = useState('');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['movie', id],
    queryFn: () => api.get<MovieDetailType>(`/api/movies/${id}`),
    // Poll only while the movie is mid-flight; otherwise stop (actions invalidate manually).
    refetchInterval: (query) => (ACTIVE_STATES.includes(query.state.data?.state ?? '') ? 5_000 : false),
  });

  const search = useMutation({
    mutationFn: () => api.post<{ releases: ReleaseDTO[] }>(`/api/movies/${id}/search`),
    onSuccess: () => {
      setMsg('');
      qc.invalidateQueries({ queryKey: ['movie', id] });
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Search failed'),
  });

  const grab = useMutation({
    mutationFn: (releaseId: string) => api.post('/api/releases/grab', { releaseId }),
    onSuccess: () => {
      setMsg('');
      qc.invalidateQueries({ queryKey: ['movie', id] });
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Grab failed'),
  });

  const patchMovie = useMutation({
    mutationFn: (patch: { monitored?: boolean; uhd?: boolean }) => api.patch(`/api/movies/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['movie', id] }),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/api/movies/${id}`),
    onSuccess: () => navigate('/'),
  });

  if (isLoading) return <Spinner />;
  if (isError || !data) return <ErrorState message="Couldn't load this movie." onRetry={() => refetch()} />;

  const grabActive = data.grab && data.grab.status !== 'imported' && data.grab.status !== 'cancelled';

  return (
    <div className="space-y-5">
      <Link to="/" className="text-xs text-slate-500 hover:text-slate-300">
        ← Library
      </Link>

      <div className="flex flex-col gap-5 sm:flex-row">
        <div className="h-72 w-48 flex-shrink-0 overflow-hidden rounded-lg bg-slate-800">
          {data.posterUrl ? (
            <img src={data.posterUrl} alt={data.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center px-2 text-center text-xs text-slate-500">
              {data.title}
            </div>
          )}
        </div>

        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold">
                {data.title} {data.year != null && <span className="text-slate-400">({data.year})</span>}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-400">
                <Badge className={MEDIA_STATE_COLORS[data.state]}>{MEDIA_STATE_LABELS[data.state]}</Badge>
                {data.runtimeMin != null && <span>{data.runtimeMin} min</span>}
                {data.importedQualityKey && (
                  <span className="text-emerald-300">have {QUALITY_LABELS[data.importedQualityKey]}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-400">Monitored</span>
              <Toggle
                checked={data.monitored}
                onChange={(v) => patchMovie.mutate({ monitored: v })}
                label="Monitor movie"
              />
              <button
                type="button"
                onClick={() => patchMovie.mutate({ uhd: !data.uhd })}
                title="Grab the 2160p (4K) version of this movie instead of 1080p (larger files)."
                className={clsx(
                  'rounded-md px-2.5 py-1 text-xs font-semibold transition',
                  data.uhd
                    ? 'bg-fuchsia-600 text-white'
                    : 'border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700',
                )}
                aria-pressed={data.uhd}
              >
                4K
              </button>
            </div>
          </div>

          {data.overview && <p className="text-sm text-slate-300">{data.overview}</p>}
          {data.lastError && <p className="text-sm text-rose-400">{data.lastError}</p>}

          <div className="flex items-center gap-3">
            <Button onClick={() => search.mutate()} disabled={search.isPending}>
              {search.isPending ? 'Searching…' : 'Search'}
            </Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => confirm('Remove this movie?') && remove.mutate()}
            >
              Remove
            </Button>
          </div>
        </div>
      </div>

      {msg && <p className="text-sm text-amber-400">{msg}</p>}

      {grabActive && data.grab && (
        <Card className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{data.grab.nzbName}</span>
            <span className={GRAB_STATUS_COLORS[data.grab.status]}>{data.grab.status}</span>
          </div>
          <ProgressBar permille={data.grab.progressPermille} />
          <div className="text-xs text-slate-500">
            {humanBytes(data.grab.downloadedBytes)} / {humanBytes(data.grab.sizeBytes)}
            {data.grab.nzbgetStatus ? ` · ${data.grab.nzbgetStatus}` : ''}
            {data.grab.error ? ` · ${data.grab.error}` : ''}
          </div>
        </Card>
      )}

      <Card>
        <h2 className="mb-2 text-sm font-semibold text-slate-300">Releases ({data.releases.length})</h2>
        {data.releases.length === 0 ? (
          <p className="text-sm text-slate-500">No candidates. Click “Search”.</p>
        ) : (
          <ReleaseTable
            releases={data.releases}
            grabbingId={grab.isPending ? (grab.variables as string) : null}
            onGrab={(releaseId) => grab.mutate(releaseId)}
          />
        )}
      </Card>
    </div>
  );
}

export function ReleaseTable({
  releases,
  grabbingId,
  onGrab,
}: {
  releases: ReleaseDTO[];
  grabbingId: string | null;
  onGrab: (releaseId: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="py-1 pr-2">Title</th>
            <th className="py-1 pr-2">Quality</th>
            <th className="py-1 pr-2">Size</th>
            <th className="py-1 pr-2">Age</th>
            <th className="py-1 pr-2">Grabs</th>
            <th className="py-1 pr-2">Score</th>
            <th className="py-1" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {releases.map((r) => (
            <tr key={r.id} className={r.rejected ? 'opacity-50' : ''}>
              <td className="max-w-md py-1.5 pr-2">
                <div className="flex items-center gap-2">
                  <span className="truncate text-slate-200" title={r.title}>
                    {r.title}
                  </span>
                  {r.isSeasonPack && (
                    <Badge className="bg-indigo-600/20 text-indigo-200">Pack</Badge>
                  )}
                  {r.hasPassword && <Badge className="bg-rose-600/20 text-rose-300">🔒</Badge>}
                </div>
                {r.rejected && r.rejectReason && <div className="text-xs text-rose-400">{r.rejectReason}</div>}
              </td>
              <td className="py-1.5 pr-2">
                <Badge className={QUALITY_COLORS[r.qualityKey]}>{QUALITY_LABELS[r.qualityKey]}</Badge>
              </td>
              <td className="py-1.5 pr-2 text-slate-400">{humanBytes(r.sizeBytes)}</td>
              <td className="py-1.5 pr-2 text-slate-400">{r.ageDays != null ? `${r.ageDays}d` : '—'}</td>
              <td className="py-1.5 pr-2 text-slate-400">{r.grabs}</td>
              <td className="py-1.5 pr-2 text-slate-400">{r.score}</td>
              <td className="py-1.5 text-right">
                <Button
                  variant="subtle"
                  disabled={r.rejected || grabbingId === r.id}
                  onClick={() => onGrab(r.id)}
                >
                  {grabbingId === r.id ? 'Grabbing…' : 'Grab'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
